import type { GetBalancesResponse } from "@defuse-protocol/one-click-sdk-typescript"
import type { TokenUsdPriceData } from "@src/components/DefuseSDK/hooks/useTokensUsdPrices"
import type {
  BaseTokenInfo,
  TokenInfo,
} from "@src/components/DefuseSDK/types/base"
import { formatTokenValue } from "@src/components/DefuseSDK/utils/format"
import getTokenUsdPrice, {
  getTokenPrice,
} from "@src/components/DefuseSDK/utils/getTokenUsdPrice"
import {
  isBaseToken,
  isUnifiedToken,
} from "@src/components/DefuseSDK/utils/token"
import type { TokenPriceChangeData } from "@src/features/token-price-changes/lib/coingeckoResponse"
import type { Holding } from "../types/sharedTypes"

export function buildPrivateBalanceMap(
  privateBalance: GetBalancesResponse | null | undefined
): Record<string, bigint> {
  const map: Record<string, bigint> = {}

  for (const entry of privateBalance?.balances ?? []) {
    map[entry.tokenId] = BigInt(entry.available)
  }

  return map
}

export function mapPrivateBalanceToHoldings(
  privateBalance: GetBalancesResponse | null | undefined,
  tokenList: TokenInfo[],
  usdPrices: TokenUsdPriceData | undefined,
  priceChanges?: TokenPriceChangeData
): Holding[] {
  const mapped: Holding[] = []

  for (const entry of privateBalance?.balances ?? []) {
    const amount = BigInt(entry.available)
    if (amount <= 0n) continue

    const token = resolveBaseTokenById(tokenList, entry.tokenId)
    if (!token) continue

    mapped.push({
      token,
      value: {
        amount,
        decimals: token.decimals,
      },
      usdPrice: getTokenPrice(token, usdPrices) ?? undefined,
      usd24hChangePct:
        priceChanges?.[token.symbol]?.usd24hChangePct ?? undefined,
      usdValue:
        getTokenUsdPrice(
          formatTokenValue(amount, token.decimals),
          token,
          usdPrices
        ) ?? undefined,
      transitValue: undefined,
      transitUsdValue: undefined,
    })
  }

  return mapped.sort((a, b) => {
    if (a.usdValue != null && b.usdValue != null) {
      return b.usdValue - a.usdValue
    }

    if (!a.value || !b.value) return 0
    return b.value.amount > a.value.amount ? 1 : -1
  })
}

function resolveBaseTokenById(
  tokenList: TokenInfo[],
  tokenId: string
): BaseTokenInfo | undefined {
  for (const token of tokenList) {
    if (isBaseToken(token) && token.defuseAssetId === tokenId) {
      return token
    }

    if (isUnifiedToken(token)) {
      const grouped = token.groupedTokens.find(
        (groupedToken) => groupedToken.defuseAssetId === tokenId
      )
      if (grouped) {
        return grouped
      }
    }
  }

  return undefined
}
